import { useGetGiftsProductRules, useGetGiftsRules } from '@agos/api-client/menu'
import { useMemo } from 'react'

import type { OrderCartLine } from './cart'
import {
  pendingAmountGiftOffers,
  pendingGiftOffers,
  withoutUnearnedAmountGifts,
  type AmountGiftRule,
  type GiftOffer,
  type ProductGiftRule,
} from './gift-rules'

/** What the cart offers and what it takes back - §9.762. */
export interface CartGifts {
  /** The gifts the order has earned and does not hold - none for somebody without *Uplatnenie darčeka*. */
  offers: GiftOffer[]
  /**
   * The cart without the amount gifts it no longer earns - **the same array** when nothing is stale, when the amount
   * rules have not answered, and always for somebody without the right, so the caller can tell nothing changed.
   */
  prunedCart: OrderCartLine[]
}

/**
 * The cart's gifts, for whoever holds *Uplatnenie darčeka* - `FRONTEND.md` §9.762, `BACKEND.md` §10A.416, owner O124,
 * FB-1081.
 *
 * **The owner**: *"Darček in košík in nová objednávka - is not showing for some čašník and for administrator its
 * showing fine. Why? Is there some hidden permission for it?"* It was *Darčeky*: both lists below were read through
 * the right to manage the gift rules, and a refused read reached the cart as *no rules*. So the cart now asks
 * `apply_gift` itself, as the server does when the order is saved - it offers exactly what the server accepts.
 *
 * - **No read and no offer without the right.** Both reads are `enabled` on it, and the rules are used only on it too:
 *   the kitchen board, which every signed-in employee opens, reads the same two keys, so a disabled read can still
 *   hand back the rules the board cached (react-query answers a cached key whether or not it may fetch it).
 * - **Nothing is taken off on rules that were not read.** `withoutUnearnedAmountGifts` returns the cart untouched for
 *   `null` rules, and the rules are `null` until the amount read has answered and always for somebody without the
 *   right - so an edit by a waiter without it keeps the Kofola the order carries (the server keeps a gift line an edit
 *   leaves alone without asking anything, §10A.416).
 * - **§9.697** - an amount gift is measured on `foodCents`, the food after both discounts and without delivery, which
 *   is the figure the server checks (§10A.354); the caller works it out from the totals it saves.
 *
 * Kept from §9.120: a count rule switched off is never offered (`pendingGiftOffers` skips it), and a count rule's read
 * that has not answered offers nothing - a count gift is loaded on an edit as an ordinary line (§9.697), so no count
 * rule ever takes one back.
 */
export function useCartGifts(cart: OrderCartLine[], foodCents: number, mayApplyGift: boolean): CartGifts {
  const { data: giftRuleData } = useGetGiftsProductRules({ query: { enabled: mayApplyGift } })
  // A switched-off rule is `pendingGiftOffers`' own refusal; the filter the page carried here only repeated it.
  const giftRules = useMemo(
    () => (mayApplyGift && Array.isArray(giftRuleData) ? (giftRuleData as ProductGiftRule[]) : []),
    [giftRuleData, mayApplyGift],
  )
  const productGiftOffers = useMemo(() => pendingGiftOffers(cart, giftRules), [cart, giftRules])

  const { data: amountGiftData, isSuccess: amountGiftsRead } = useGetGiftsRules({ query: { enabled: mayApplyGift } })
  const amountGiftRules = useMemo(
    () =>
      mayApplyGift && amountGiftsRead && Array.isArray(amountGiftData) ? (amountGiftData as AmountGiftRule[]) : null,
    [amountGiftData, amountGiftsRead, mayApplyGift],
  )
  const offers = useMemo(
    () => [
      ...productGiftOffers,
      ...(amountGiftRules === null ? [] : pendingAmountGiftOffers(cart, amountGiftRules, foodCents)),
    ],
    [productGiftOffers, amountGiftRules, cart, foodCents],
  )

  return { offers, prunedCart: withoutUnearnedAmountGifts(cart, amountGiftRules, foodCents) }
}
